/**
 * useNetworkStatus Hook
 *
 * Monitors browser network status and connection quality.
 * Uses navigator.onLine and Navigator.connection API.
 *
 * Features:
 * - Online/offline status detection
 * - Connection type detection (WiFi vs cellular)
 * - Effective connection speed (4G, 3G, etc.)
 * - Automatic status updates on network changes
 */

import { useState, useEffect } from 'react';
import type { NetworkStatus } from '../types/offline';

/** Experimental Connection API shape (not in standard lib.dom.d.ts) */
interface NavigatorConnection {
  type?: 'wifi' | 'cellular' | 'unknown';
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  addEventListener: (event: string, listener: () => void) => void;
  removeEventListener: (event: string, listener: () => void) => void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NavigatorConnection;
  mozConnection?: NavigatorConnection;
  webkitConnection?: NavigatorConnection;
}

/**
 * Hook for monitoring network status
 *
 * Automatically updates when network state changes.
 * Combines navigator.onLine with Connection API for comprehensive status.
 *
 * @returns Current network status including online state and connection details
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { isOnline, connectionType, effectiveType } = useNetworkStatus();
 *
 *   return (
 *     <div>
 *       {isOnline ? (
 *         <p>Connected via {connectionType}</p>
 *       ) : (
 *         <p>Offline</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    connectionType: 'unknown',
  });

  useEffect(() => {
    /**
     * Update network status from browser APIs
     */
    const updateNetworkStatus = () => {
      // Get connection info from Navigator.connection API (if available)
      // Note: This is an experimental API with limited browser support
      const nav = navigator as NavigatorWithConnection;
      const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

      setStatus({
        isOnline: navigator.onLine,
        connectionType: connection?.type || 'unknown',
        effectiveType: connection?.effectiveType,
      });
    };

    // Initial detection on mount
    updateNetworkStatus();

    // Listen to online/offline events
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Listen to connection changes (if Connection API is supported)
    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection) {
      connection.addEventListener('change', updateNetworkStatus);
    }

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  return status;
}
